// client/src/components/pages/Geofencing/zoneTypes.js
// LexiStar v2 zone taxonomy — shared labels, colors, and helpers for the
// geofence zone types returned by the server (`ZoneType`, submitted as
// `zoneType`). Zones with a missing ZoneType fall back to 'stockpile',
// matching the server default.
import React from 'react';

export const DEFAULT_ZONE_TYPE = 'stockpile';

// `label` = plural (layer chips), `singular` = badge/select label,
// `color` = accent hex (chip dots), `badgeClass` = full Tailwind classes
// (literal strings so the JIT scanner picks them up).
export const ZONE_TYPES = [
  {
    id: 'site_boundary',
    label: 'Site boundaries',
    singular: 'Site boundary',
    color: '#0ea5e9',
    badgeClass: 'bg-sky-100 text-sky-700',
  },
  {
    id: 'stockpile',
    label: 'Stockpiles',
    singular: 'Stockpile',
    color: '#14b8a6',
    badgeClass: 'bg-teal-100 text-teal-700',
  },
  {
    id: 'weighbridge',
    label: 'Weighbridges',
    singular: 'Weighbridge',
    color: '#8b5cf6',
    badgeClass: 'bg-purple-100 text-purple-700',
  },
  {
    id: 'loading_bay',
    label: 'Loading bays',
    singular: 'Loading bay',
    color: '#f59e0b',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'workshop',
    label: 'Workshops',
    singular: 'Workshop',
    color: '#64748b',
    badgeClass: 'bg-slate-200 text-slate-700',
  },
  {
    id: 'no_go',
    label: 'No-go',
    singular: 'No-go',
    color: '#ef4444',
    badgeClass: 'bg-red-100 text-red-700',
  },
  {
    id: 'custom',
    label: 'Custom',
    singular: 'Custom',
    color: '#6366f1',
    badgeClass: 'bg-indigo-100 text-indigo-700',
  },
];

// LexiStar purpose type -> the layer group (zone type) it shows under when a
// row carries no ZoneType (the server keeps zone_type in sync, so this is only a
// fallback). Mirrors PURPOSE_TO_ZONE in ./purposeTypes; unmapped -> 'custom'.
const PURPOSE_LAYER = {
  site: 'site_boundary',
  source: 'stockpile',
  weighbridge: 'weighbridge',
  maintenance: 'workshop',
  no_go: 'no_go',
};

/** Zone type id (= map layer group) for a geofence row, with the server-default fallback. */
export const getZoneType = (zone) =>
  zone?.ZoneType ||
  (zone?.PurposeType ? PURPOSE_LAYER[zone.PurposeType] || 'custom' : DEFAULT_ZONE_TYPE);

/** Taxonomy entry for a type id (falls back to the stockpile entry). */
export const getZoneTypeMeta = (id) =>
  ZONE_TYPES.find((t) => t.id === id) || ZONE_TYPES.find((t) => t.id === DEFAULT_ZONE_TYPE);

/** Small colored pill naming a zone type. */
export function ZoneTypeBadge({ type }) {
  const meta = getZoneTypeMeta(type);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${meta.badgeClass}`}
    >
      {meta.singular}
    </span>
  );
}
